import { forwardRef, type ReactNode, type ReactElement } from 'react';
import { isElement } from 'react-is';

import { useCurrentTheme } from '@dynatrace/strato-components/core';
import {
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

import { generateVisualPresetCdnUrl } from '../empty-state-utils.js';

/**
 * @public
 */
export interface EmptyStateVisualPresetProps extends StylingProps, DataTestId {
  /**
   * The visual context of the illustration.
   */
  context: 'chart' | 'document' | 'generic' | 'table' | 'query';
  /**
   * A visual cue added to the illustration hinting at the reason the state is empty.
   */
  type:
    | 'something-missing'
    | 'no-result'
    | 'something-wrong'
    | 'create-new'
    | 'no-permission';
}
//#endregion Typings

/**
 * Typeguard function that will check if a ReactNode is a EmptyState.VisualPreset
 * @internal
 */
export function isEmptyStateVisualPreset(
  child: ReactNode,
): child is ReactElement<EmptyStateVisualPresetProps> {
  return isElement(child) && child.type === VisualPreset;
}

/**
 * Container for the Visual slot of the EmptyState component.
 * @public
 */
export const VisualPreset = /* @__PURE__ */ forwardRef<
  HTMLImageElement,
  EmptyStateVisualPresetProps
>((props, forwardedRef) => {
  const {
    context,
    type,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props;

  const currentTheme = useCurrentTheme();
  const visualPresetUrl = generateVisualPresetCdnUrl(
    context,
    type,
    currentTheme,
  );

  return (
    <img
      // no meaningful alt text for screen readers
      aria-hidden
      alt=""
      ref={forwardedRef}
      src={visualPresetUrl}
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
    />
  );
});

(VisualPreset as typeof VisualPreset & { displayName: string }).displayName =
  'EmptyState.VisualPreset';
